import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import styled, { css } from 'styled-components';
import { InfoIcon, WarningIcon, ErrorIcon, SuccessIcon, CloseSmallIcon } from './utils/IconFactory';
import { isMobile, FadeIn, fadeFromLeft } from "./utils";
export default React.memo(function NotificationPopup({ type = 'info', size = 'medium', title, content, buttons, onClose, className = '', footer, }) {
    const isMobileDevice = isMobile();
    React.useEffect(() => {
        const escapeHandler = (evt) => {
            if (evt.keyCode == 27) {
                onClose === null || onClose === void 0 ? void 0 : onClose();
            }
        };
        if (onClose) {
            document.addEventListener('keydown', escapeHandler, false);
            return () => {
                document.removeEventListener("keydown", escapeHandler, false);
            };
        }
    }, []);
    const typeColor = React.useMemo(() => {
        return type === 'info' ?
            'var(--ui-accent-color)' :
            type === 'warning' ?
                'var(--ui-color-yellow)' :
                type === 'error' ?
                    'var(--ui-color-red)' :
                    type === 'success' ?
                        'var(--ui-color-green)' : 'inherit';
    }, [type]);
    return (_jsx(OverLay, { className: `${className} ui-notification-popup`, visible: true, children: _jsxs(Popup, { type: type, size: size, typeColor: typeColor, isMobileDevice: isMobileDevice, children: [_jsxs("header", { children: [_jsx("div", { className: "icon", children: type === 'info' ?
                                _jsx(InfoIcon, {}) :
                                type === 'warning' ?
                                    _jsx(WarningIcon, {}) :
                                    type === 'error' ?
                                        _jsx(ErrorIcon, {}) :
                                        type === 'success' ?
                                            _jsx(SuccessIcon, {}) : _jsx(InfoIcon, {}) }), Boolean(title) && _jsx("div", { className: "title", children: title }), onClose && _jsx(CloseButton, { onClick: () => onClose(), children: _jsx(CloseSmallIcon, {}) })] }), _jsx("div", { className: "content", children: content }), _jsx("div", { className: "buttons", children: buttons }), footer && _jsx(Footer, { className: "footer", children: footer })] }) }));
});
const OverLay = styled.div `
  display: flex;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;

  justify-content: center;
  align-items: center;

  background: var(--ui-overlay-color);

  ${p => !p.visible && css `
    background: transparent;
    pointer-events: none;
  `}
`;
const Popup = styled.div `
  display: flex;
  box-sizing: border-box;
  position: absolute;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0 2.813rem;

  width: 31.5rem;
  max-width: 90%;
  
  min-height: 34.25rem;
  background: var(--ui-popup-bg-color);
  box-shadow: var(--ui-box-shadow);
  border-radius: 0.625rem;

  animation: ${p => p.size === 'small' ? fadeFromLeft : FadeIn} 0.2s ease-in-out;

  ${p => p.isMobileDevice && css `
    width: 100vw;
    height: 100vh;
    border-radius: 0;
  `};

  & header {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  & .icon {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 6rem;
    height: 6rem;
    border-radius: 50%;
    background: var(--ui-accent-color);
    box-shadow: var(--ui-box-shadow-dark);
    color: var(--ui-color-white);

    ${p => p.type === 'warning' && css `
      background: var(--ui-color-yellow);
    `}

    ${p => p.type === 'error' && css `
      background: var(--ui-color-red);
    `}

    ${p => p.type === 'success' && css `
      background: var(--ui-color-green);
    `}

    & svg {
      width: 2rem;
      height: 2rem;
      vertical-align: middle;
      
    }

    ${p => p.size === 'small' && css `
      width: 1.875rem;
      height: 1.875rem;
      border-radius: 0;
      background: transparent;
      box-shadow: none;

      & svg {
        width: 100%;
        height: 100%;
        vertical-align: middle;
        color: ${p.typeColor};
      }
    `}
  }

  & .title {
    /* text-align: center; */
    font-size: 1.5rem;
    line-height: 1.703rem;
    font-weight: 500;
    color: var(--text-primary);
    margin-top: 2.125rem;
  }

  & .content {
    position: relative;
    text-align: center;
    margin-top: 0.875rem;
    font-size: 1rem;
    line-height: 1.375rem;
    color: var(--text-secondary);

    & a {
      color: var(--ui-accent-color);
    }
  }

  & .buttons {
    display: flex;
    margin-top: 1.5rem;
  }

  ${p => p.size === 'small' && css `
    display: block;
    width: 32.625rem;
    min-height: 15.5rem;
    padding: 2.438rem 2.063rem;

    & header {
      align-items: flex-start;
    }

    & .title {
      margin-top: 1.313rem;
    }

    & .content {
      text-align: left;
      margin-top: 0.375rem;
    }
  `}
`;
const CloseButton = styled.div `
  display: flex;
  position: absolute;
  width: 1.5rem;
  height: 1.5rem;
  justify-content: center;
  align-items: center;
  right: 0;
  top: 0;
  cursor: pointer;

  & svg {
    width: 1.5rem;
    height: 1.5rem;
  }

  &:hover {
    opacity: 0.87;
  }
`;
const Footer = styled.div `
  position: absolute;
  bottom: 0;
  width: 100%;
  background-color: var(--layer-color);
  border-bottom-left-radius: 0.625rem;
  border-bottom-right-radius: 0.625rem;
  text-align: center;
  font-size: 0.8rem;
  line-height: 1.375rem;
  color: var(--text-secondary);
  padding: 0.8rem 0;

  & a {
    color: var(--ui-accent-color);
  }
`;
